/**
 * MountainBiome - The second biome (500-1000 miles)
 *
 * Mountain characteristics:
 * - Dramatic elevation changes with peaks and valleys
 * - Winding switchback roads
 * - Pine trees, boulders, and alpine vegetation
 * - Cool blue-green color palette
 */

import { BiomeRenderer } from '../BiomeRenderer.js';
import { createBiomeConfig, validateBiomeConfig } from './BiomeConfig.js';

// Mountain-specific color palettes for each time of day
// Index: 0=Day, 1=Sunset, 2=Night, 3=Dawn
export const MOUNTAIN_COLORS = [
    // ═══════════════════════════════════════════════════════════════════════════
    // DAY - Cool alpine greens and grays
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Alpine meadow greens
            0x4a6b3a, 0x5a7b4a, 0x3d5e2d,
            // Rocky gray-greens
            0x5a6b5a, 0x4a5b4a, 0x6a7b6a,
            // Forest floor browns
            0x5a5040, 0x4a4535, 0x6a6050
        ],
        rockColors: [
            0x6a6a70,   // Mountain granite
            0x7a7a80,   // Light stone
            0x5a5a60,   // Dark granite
            0x8a8a90    // Pale boulder
        ],
        shrubColors: [
            0x2d4a2d,   // Dark pine
            0x3a5a3a,   // Forest green
            0x4a6a4a,   // Light pine
            0x354535    // Shadow pine
        ]
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // SUNSET - Warm alpenglow on peaks
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Golden-lit meadows
            0x6a7b4a, 0x7a8b5a, 0x5a6a3a,
            // Warm shadowed valleys
            0x5a5a48, 0x4a4a38, 0x6a6a58,
            // Alpenglow peaks
            0x8a7060, 0x9a8070, 0x7a6050
        ],
        rockColors: [
            0x8a7060,   // Warm granite
            0x7a6050,   // Shadowed stone
            0x9a8070,   // Sunset-lit rock
            0x6a5040    // Deep shadow
        ],
        shrubColors: [
            0x3a4a30,   // Shadowed pine
            0x4a5a40,   // Warm pine
            0x2a3a20,   // Dark forest
            0x354530    // Twilight pine
        ]
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // NIGHT - Cool moonlit blues
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Moonlit blue-greens
            0x3a4a50, 0x4a5a60, 0x2a3a40,
            // Dark valleys
            0x2a3a3a, 0x3a4a4a, 0x1a2a2a,
            // Silvered peaks
            0x4a5a68, 0x5a6a78, 0x3a4a58
        ],
        rockColors: [
            0x4a4a58,   // Night granite
            0x3a3a48,   // Dark stone
            0x5a5a68,   // Moonlit rock
            0x2a2a38    // Shadow rock
        ],
        shrubColors: [
            0x1a2a1a,   // Very dark pine
            0x2a3a2a,   // Night forest
            0x0a1a0a,   // Deep shadow
            0x1a2a20    // Dark blue-green
        ]
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // DAWN - Soft misty morning
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Misty meadows
            0x6a7a6a, 0x7a8a7a, 0x5a6a5a,
            // Soft morning greens
            0x5a6a50, 0x6a7a60, 0x4a5a40,
            // Pale peaks
            0x8a9a8a, 0x9aaa9a, 0x7a8a7a
        ],
        rockColors: [
            0x7a7a80,   // Morning granite
            0x8a8a90,   // Pale stone
            0x6a6a70,   // Cool gray
            0x9a9aa0    // Misty rock
        ],
        shrubColors: [
            0x3a4a3a,   // Morning pine
            0x4a5a4a,   // Soft green
            0x2a3a2a,   // Shadowed
            0x4a5a48    // Pale pine
        ]
    }
];

export class MountainBiome extends BiomeRenderer {
    constructor(scene, camera, worldTranslator, config = {}) {
        // Mountain-specific configuration
        const mountainConfig = createBiomeConfig({
            // ─────────────────────────────────────────────────────────────
            // ROAD - Winding mountain passes with elevation changes
            // Uses hash-based noise with prime intervals for non-repeating paths
            // ─────────────────────────────────────────────────────────────
            roadWidth: 9,                        // Slightly narrower mountain roads
            chunkLength: 50,
            pathSegmentLength: 150,              // Spline control point spacing
            pathLookAhead: 100,
            roadMaxLateralOffset: 220,           // Winding mountain curves
            roadMaxVerticalOffset: 50,           // More dramatic elevation changes
            roadSplineTension: 0.0,              // Zero tension = tight spline following

            // ─────────────────────────────────────────────────────────────
            // TERRAIN - Dramatic peaks and valleys
            // ─────────────────────────────────────────────────────────────
            terrainColor: 0x4a5d3a,              // Mountain green/gray
            terrainAmplitudeLarge: 600,          // Tall mountains
            terrainAmplitudeMedium: 300,         // Rolling foothills
            terrainAmplitudeSmall: 60,           // Rocky outcrops
            terrainAmplitudeFine: 15,            // Surface detail
            terrainHeightMultiplier: 1.2,        // Overall taller terrain
            waterLevel: -20,                     // Mountain streams in deep valleys

            // ─────────────────────────────────────────────────────────────
            // TERRAIN DIMENSIONS - Balance visibility vs performance
            // ─────────────────────────────────────────────────────────────
            terrainWidth: 2000,                  // Total width of terrain on each side of road (units)
            terrainSegmentsOut: 20,              // Segments perpendicular to road (higher = smoother)
            terrainSegmentsAlong: 1,             // Segments along road direction (1 = low-poly chunk-wide faces)

            // ─────────────────────────────────────────────────────────────
            // ROADSIDE STRIP - High-res terrain bridging road to mountains
            // This strip follows road curves precisely, preventing gaps during turns
            // ─────────────────────────────────────────────────────────────
            roadsideStripWidth: 60,              // Width of high-res strip next to road (units)
            roadsideStripSegments: 4,            // Segments perpendicular within strip (higher = smoother)
            roadsideStripOverlap: 0.15,          // How far strip extends under road edge (prevents gaps)
            mountainOverlapIntoStrip: 5,         // How far mountains extend into strip (hides seams)

            // ─────────────────────────────────────────────────────────────
            // TERRAIN FEATURES - Mountain-appropriate formations
            // Emphasize peaks, cliffs, valleys - dramatic alpine terrain
            // ─────────────────────────────────────────────────────────────
            terrainFeaturesEnabled: true,
            terrainFeatureScale: 0.00005,        // Feature region size
            terrainFeatureWeights: {
                dunes: 0.2,       // Rare - not typical for mountains
                peaks: 1.2,       // Dominant - jagged mountain peaks
                plateaus: 0.5,    // Some high alpine plateaus
                spires: 0.7,      // Rocky spires and crags
                cliffs: 0.9,      // Cliff faces and escarpments
                valleys: 0.8      // Deep mountain valleys
            },
            terrainFeatureBlend: 0.35,           // Blend between features

            // ─────────────────────────────────────────────────────────────
            // TERRAIN HEIGHT LAYERING - Elevation increases with distance from road
            // Layers: near-road, mid-distance, far, horizon
            // ─────────────────────────────────────────────────────────────
            terrainLayerHeights: [0.4, 0.7, 1.0, 1.6],       // Height multiplier per layer
            terrainLayerDistances: [0.05, 0.15, 0.4, 1.0],   // Distance thresholds per layer

            // ─────────────────────────────────────────────────────────────
            // FLAT ZONE - Gap between road edge and where terrain starts rising
            // Controls how close mountains/terrain can get to the road
            // ─────────────────────────────────────────────────────────────
            terrainFlatZoneMin: 0.02,            // Minimum flat distance (fraction of width)
            terrainFlatZoneMax: 0.08,            // Maximum flat distance (fraction of width)
            terrainBlendRange: 0.05,             // How gradually terrain rises from flat zone

            // ─────────────────────────────────────────────────────────────
            // ROADSIDE OBJECTS - Pine trees and boulders
            // ─────────────────────────────────────────────────────────────
            enableRoadsideObjects: true,
            roadsideObjectSpawnDistance: 700,
            roadsideObjectDensity: 0.5,          // Denser vegetation than desert
            roadsideObjectMinSafeZone: 10,
            roadsideObjectDistributionWidth: 180,
            rockProbability: 0.35,               // More trees, fewer rocks
            rockMinSize: 3,                      // Larger boulders
            rockMaxSize: 10,
            shrubMinSize: 6,                     // Taller pine trees
            shrubMaxSize: 18,
            shrubSpriteCount: 4,
            shrubScaleVariation: 0.4,
            shrubStretchVariation: 0.15,         // Less stretch (trees are more uniform)
            roadsideObjectFadeInDuration: 0.5,

            // Mountain-specific sprites (pine trees)
            // TODO: Create mountain-specific sprites - using desert as placeholder
            shrubSpritePaths: [
                'assets/sprites/shrub1.png',     // Replace with pine1.png
                'assets/sprites/shrub2.png',     // Replace with pine2.png
                'assets/sprites/shrub3.png',     // Replace with pine3.png
                'assets/sprites/shrub4.png',     // Replace with pine4.png
                'assets/sprites/shrub5.png'      // Replace with pine5.png
            ],

            // Merge with any user-provided config
            ...config
        });

        // Validate configuration in development
        validateBiomeConfig(mountainConfig, 'MountainBiome');

        super(scene, camera, worldTranslator, mountainConfig);

        // Biome identity
        this.biomeName = 'Mountain';
        this.minMiles = 500;
        this.maxMiles = 1000;

        // Store biome-specific color palettes (via palette manager)
        this.biomeColors = MOUNTAIN_COLORS;
        this.palette.setBiomeColors(MOUNTAIN_COLORS);
    }

    /**
     * Get terrain colors for a specific time of day
     * @param {number} timeIndex - 0=Day, 1=Sunset, 2=Night, 3=Dawn
     * @returns {number[]} Array of terrain color hex values
     */
    getTerrainColors(timeIndex) {
        const palette = this.biomeColors[timeIndex];
        return palette ? palette.terrainColors : [];
    }

    /**
     * Get rock colors for a specific time of day
     * @param {number} timeIndex - 0=Day, 1=Sunset, 2=Night, 3=Dawn
     * @returns {number[]} Array of rock color hex values
     */
    getRockColors(timeIndex) {
        const palette = this.biomeColors[timeIndex];
        return palette ? palette.rockColors : [];
    }

    /**
     * Get shrub colors for a specific time of day
     * @param {number} timeIndex - 0=Day, 1=Sunset, 2=Night, 3=Dawn
     * @returns {number[]} Array of shrub color hex values
     */
    getShrubColors(timeIndex) {
        const palette = this.biomeColors[timeIndex];
        return palette ? palette.shrubColors : [];
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // OPTIONAL OVERRIDES
    // Uncomment and customize these methods to add biome-specific behavior
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Override terrain height generation for mountain-specific features
     * Could add snow caps, cliff faces, or other mountain features
     */
    // generateTerrainHeight(x, z) {
    //     const baseHeight = super.generateTerrainHeight(x, z);
    //
    //     // Example: Add snow caps above certain height
    //     // if (baseHeight > 300) {
    //     //     // Make terrain more jagged at high altitudes
    //     //     const snowNoise = this.noise.noise(x * 0.1, z * 0.1) * 20;
    //     //     return baseHeight + snowNoise;
    //     // }
    //
    //     return baseHeight;
    // }

    /**
     * Override to add biome-specific visual effects
     * Could add fog banks, falling snow, etc.
     */
    // update(deltaTime) {
    //     super.update(deltaTime);
    //
    //     // Example: Add mountain-specific effects here
    //     // this.updateSnowParticles(deltaTime);
    //     // this.updateFogBanks(deltaTime);
    // }

    /**
     * Override to customize object spawning
     * Could add special mountain objects like pine trees, cabins, etc.
     */
    // spawnRoadsideObject(position, roadCenter) {
    //     // Call parent for default behavior
    //     super.spawnRoadsideObject(position, roadCenter);
    //
    //     // Or implement custom spawning logic
    // }
}

/**
 * Export biome registry entry for easy registration
 */
export const MOUNTAIN_BIOME_ENTRY = {
    minMiles: 500,
    maxMiles: 1000,
    BiomeClass: MountainBiome,
    name: 'Mountain'
};
